/**
 * Layer toggle panel — bottom-right stack above the style switcher.
 * Each toggle shows/hides a named data layer via registered callbacks.
 */
import type mapboxgl from 'mapbox-gl';

interface LayerDef {
  id: string;
  label: string;
  icon: string;
  enabled: boolean;
  primary?: boolean;   // Render in the bolder "topic" group with action buttons
  onEnable: (map: mapboxgl.Map) => void;
  onDisable: (map: mapboxgl.Map) => void;
}

const layers: LayerDef[] = [];
let mapRef: mapboxgl.Map | null = null;

/** Register a toggleable layer. Call before initLayerPanel().
 *  Pass `primary: true` to render in the bolder "topic" group with action buttons. */
export function registerLayer(def: Omit<LayerDef, 'enabled'> & { defaultEnabled?: boolean }): void {
  layers.push({ ...def, enabled: def.defaultEnabled ?? false });
}

/** Check if a layer is currently enabled (used for style-change re-add) */
export function isLayerEnabled(id: string): boolean {
  return layers.find(l => l.id === id)?.enabled ?? false;
}

interface ActionDef {
  id: string;
  label: string;
  icon: string;
  onClick: () => void;
}

const actions: ActionDef[] = [];

/** Register a non-toggle action button (e.g. Resources). Call before initLayerPanel(). */
export function registerAction(def: ActionDef): void {
  actions.push(def);
}

/** Build and mount the toggle panel. Call once after map is ready. */
export function initLayerPanel(map: mapboxgl.Map): void {
  mapRef = map;

  document.getElementById('layer-panel')?.remove();

  const panel = document.createElement('div');
  panel.id = 'layer-panel';

  const renderAction = (a: ActionDef) => `
    <button
      class="layer-toggle-btn layer-action-btn layer-primary-btn"
      data-action="${a.id}"
      title="${a.label}"
    >
      <span class="layer-toggle-icon">${a.icon}</span>
      <span class="layer-toggle-label">${a.label}</span>
    </button>
  `;
  const renderLayer = (l: LayerDef, primaryClass: string = '') => `
    <button
      class="layer-toggle-btn${l.enabled ? ' active' : ''}${primaryClass ? ' ' + primaryClass : ''}"
      data-layer="${l.id}"
      title="${l.label}"
    >
      <span class="layer-toggle-icon">${l.icon}</span>
      <span class="layer-toggle-label">${l.label}</span>
    </button>
  `;

  // Group 1 — bolder "topic" group: actions + any layer flagged primary
  const actionHtml = actions.map(renderAction).join('');
  const primaryLayers = layers.filter(l => l.primary);
  const otherLayers   = layers.filter(l => !l.primary);
  const primaryLayerHtml = primaryLayers.map(l => renderLayer(l, 'layer-primary-btn')).join('');

  // Group 2 — tools (Airspace, Measure)
  const otherLayerHtml = otherLayers.map(l => renderLayer(l)).join('');

  panel.innerHTML = `
    <div class="layer-group layer-group-primary">
      ${actionHtml}${primaryLayerHtml}
    </div>
    <div class="layer-group-separator"></div>
    <div class="layer-group">
      ${otherLayerHtml}
    </div>
  `;

  document.getElementById('map')!.appendChild(panel);

  // Handle layer toggle clicks
  panel.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('.layer-toggle-btn');
    if (!btn || !mapRef) return;

    // Action button?
    const actionId = btn.dataset.action;
    if (actionId) {
      const action = actions.find(a => a.id === actionId);
      action?.onClick();
      return;
    }

    // Layer toggle
    const id = btn.dataset.layer!;
    const layer = layers.find(l => l.id === id);
    if (!layer) return;

    layer.enabled = !layer.enabled;
    btn.classList.toggle('active', layer.enabled);

    if (layer.enabled) {
      layer.onEnable(mapRef);
    } else {
      layer.onDisable(mapRef);
    }
  });
}
